/**
 * GDPR Data Request API Service
 * Implements GDPR Articles 15, 16, 17, 20
 */
import api from './api';

export type ResourceType = 'member' | 'coupon' | 'ticket';
export type ExportFormat = 'csv' | 'json';

export interface GDPRAccessResponse {
  external_id: string;
  resource_type: ResourceType;
  program_id?: string;
  data: any;
  retrieved_at: string;
}

export interface GDPRDeleteResponse {
  message: string;
  external_id: string;
  resource_type: ResourceType;
  deleted_at: string;
  wallet_invalidation: string;
}

export interface GDPRUpdateRequest {
  person?: {
    forename?: string;
    surname?: string;
    emailAddress?: string;
    mobileNumber?: string;
    displayName?: string;
  };
  tier_id?: string;
}

export interface GDPRUpdateResponse {
  message: string;
  external_id: string;
  resource_type: ResourceType;
  updated_fields: string[];
  updated_at: string;
  wallet_update: string;
}

export interface GDPRExportResponse {
  format: string;
  external_id: string;
  resource_type: ResourceType;
  data: any;
  exported_at: string;
}

/**
 * GDPR Right to Access (Article 15)
 * Retrieve all data for a resource
 * SECURE: Uses POST with body instead of GET with email in URL
 */
export const accessData = async (
  externalId: string,
  resourceType: ResourceType
): Promise<GDPRAccessResponse> => {
  const response = await api.post('/gdpr/data/access', {
    external_id: externalId,
    resource_type: resourceType,
  });
  return response.data;
};

/**
 * GDPR Right to Erasure (Article 17 - "Right to be Forgotten")
 * Delete all data and invalidate wallet pass
 */
export const deleteData = async (
  externalId: string,
  resourceType: ResourceType
): Promise<GDPRDeleteResponse> => {
  const response = await api.delete(
    `/gdpr/${resourceType}/${externalId}`
  );
  return response.data;
};

/**
 * GDPR Right to Portability (Article 20)
 * Export data in machine-readable format
 */
export const exportData = async (
  externalId: string,
  resourceType: ResourceType,
  format: ExportFormat = 'csv'
): Promise<Blob | GDPRExportResponse> => {
  const response = await api.get(
    `/gdpr/${resourceType}/${externalId}/export`,
    {
      params: { format },
      responseType: format === 'csv' ? 'blob' : 'json',
    }
  );

  if (format === 'csv') {
    return response.data as Blob;
  }
  return response.data as GDPRExportResponse;
};

/**
 * GDPR Right to Rectification (Article 16)
 * Update data and push changes to wallet
 */
export const updateData = async (
  externalId: string,
  resourceType: ResourceType,
  updateData: GDPRUpdateRequest
): Promise<GDPRUpdateResponse> => {
  const response = await api.patch(
    `/gdpr/${resourceType}/${externalId}`,
    updateData
  );
  return response.data;
};

/**
 * Download exported CSV file
 */
export const downloadCSV = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const gdprAPI = {
  accessData,
  deleteData,
  exportData,
  updateData,
  downloadCSV,
};

export default gdprAPI;
